import { CENTER_OF_EXCELLENCE } from '../../centerOfExcellence/types'
import { TOOLKIT_TYPE, WELLNESS_TOOLKIT_PROMOTION } from '../../wellnessToolkit/types'
import { SEARCHABLE_PAGES } from '../searchablePages'
import { SEARCHABLE_PAGE_ID, SearchablePageMetadata } from '../types'
import { EligibilityDeps } from './types'

const isHealthPlanPaymentVisible = (deps: EligibilityDeps): boolean =>
  !deps.isHideCostAndSessionLimitEnabled &&
  !deps.isInternational &&
  (deps.healthPlanExtensionEnabled ||
    deps.hasCostSharingCard ||
    (deps.hasBcxProvider && !(deps.infractionMessagingDisabled && deps.cancellationLimitCoversSessionLimit)))

const isInfractionCardSectionVisible = (deps: EligibilityDeps): boolean =>
  deps.hasBcxProvider &&
  !deps.isInternational &&
  (!deps.infractionMessagingDisabled ||
    (deps.infractionMessagingDisabled && deps.healthPlanExtensionEnabled && deps.hasUpcomingHPIAppointments))

/**
 * Map of page ID to eligibility function.
 * If a page is not in this map, it is considered always eligible (requires auth only).
 */
const PAGE_ELIGIBILITY: Partial<Record<SEARCHABLE_PAGE_ID, (deps: EligibilityDeps) => boolean>> = {
  // ============ TRIAGE ============
  [SEARCHABLE_PAGE_ID.ADULT_TRIAGE]: (deps) => deps.isSelfSearchAvailable,
  [SEARCHABLE_PAGE_ID.CHILD_TRIAGE]: (deps) => !deps.shouldHideChildSearchCTA,

  // ============ PROGRAMS (DPLs) ============
  [SEARCHABLE_PAGE_ID.COACHING]: (deps) => deps.isCoachingEnabled,
  [SEARCHABLE_PAGE_ID.PARENTING_COACHING]: (deps) => deps.isCoachingForParentsEnabled,
  [SEARCHABLE_PAGE_ID.COUPLES_THERAPY]: (deps) => deps.isDirectAccessTherapyEnabled,
  [SEARCHABLE_PAGE_ID.FAMILY_THERAPY]: (deps) => deps.isDirectAccessTherapyEnabled,
  [SEARCHABLE_PAGE_ID.MEDICATION_CONSULT]: (deps) => deps.isMedsEnabled,
  [SEARCHABLE_PAGE_ID.RENEW_AUD]: (deps) => deps.isAUDSupported && !deps.isUserTeen,

  // ============ COMPLEX CARE / EXTERNAL ============
  [SEARCHABLE_PAGE_ID.COMPLEX_CARE]: (deps) => deps.isComplexCareEnabled,
  [SEARCHABLE_PAGE_ID.DBT]: (deps) => deps.isComplexCareEnabled,
  [SEARCHABLE_PAGE_ID.IOP]: (deps) => deps.isComplexCareEnabled,
  [SEARCHABLE_PAGE_ID.PHP]: (deps) => deps.isComplexCareEnabled,
  [SEARCHABLE_PAGE_ID.RESIDENTIAL]: (deps) => deps.isComplexCareEnabled,
  [SEARCHABLE_PAGE_ID.INPATIENT]: (deps) => deps.isComplexCareEnabled,

  // ============ LIBRARY ============
  [SEARCHABLE_PAGE_ID.LIBRARY]: (deps) => deps.isEssentialsEnabled,
  [SEARCHABLE_PAGE_ID.MEDITATION]: (deps) => deps.isEssentialsEnabled,
  [SEARCHABLE_PAGE_ID.EVENTS]: (deps) => deps.isLibraryEventsEnabled,

  // ============ CARE ============
  [SEARCHABLE_PAGE_ID.CARE_TAB]: (deps) => deps.shouldShowCareTabInitial || deps.activitiesEnabled,
  [SEARCHABLE_PAGE_ID.SESSIONS]: (deps) => deps.showSessions,
  [SEARCHABLE_PAGE_ID.WELLNESS_CHECK_IN]: (deps) =>
    deps.isWellnessCheckInEnabled && deps.isEssentialsEnabled && deps.isWellnessCheckInLanguageSupported,

  // ============ ACCOUNT ============
  [SEARCHABLE_PAGE_ID.SESSION_HISTORY]: (deps) =>
    (deps.hasSessionHistory || !deps.isInternational || deps.hasAccessToInternationallySupportedTreatmentOptions) &&
    !deps.forceAdultFlowsToHPI,
  [SEARCHABLE_PAGE_ID.COVERAGE_INFO]: (deps) =>
    !deps.isHealthPlanDirect &&
    !deps.adultTriageDisabled &&
    !deps.forceAdultFlowsToHPI &&
    !deps.isHideCostAndSessionLimitEnabled,
  [SEARCHABLE_PAGE_ID.HEALTH_PLAN_PAYMENT]: isHealthPlanPaymentVisible,
  [SEARCHABLE_PAGE_ID.ADD_DEPENDENT]: (deps) =>
    !deps.shouldHideChildSearchCTA &&
    !deps.isUserMinor &&
    !deps.isInternational &&
    !deps.hideDependentField &&
    deps.isFamilyHubEnabled,
  [SEARCHABLE_PAGE_ID.DISPLAY_LANGUAGE]: (deps) => deps.isDisplayLanguageEnabled,
  [SEARCHABLE_PAGE_ID.ADD_HEALTH_PLAN]: (deps) => isHealthPlanPaymentVisible(deps) && deps.isClientHPI,
  [SEARCHABLE_PAGE_ID.ADD_PAYMENT_METHOD]: (deps) => isHealthPlanPaymentVisible(deps) && deps.isClientHPI,
  [SEARCHABLE_PAGE_ID.LATE_CANCELLATION_PAYMENT]: (deps) =>
    isHealthPlanPaymentVisible(deps) && isInfractionCardSectionVisible(deps),
  [SEARCHABLE_PAGE_ID.NOTIFICATIONS]: (deps) => deps.hasEpisode,
  [SEARCHABLE_PAGE_ID.PRIVACY_LEGAL]: (deps) => deps.hasBcxProvider,
  [SEARCHABLE_PAGE_ID.CLINICAL_DATA_SHARING]: (deps) => deps.isClientDataShareEnabled && !deps.isUserMinor,
  [SEARCHABLE_PAGE_ID.FORKED_TRIAGE]: (deps) => deps.showForkedTriage,
  [SEARCHABLE_PAGE_ID.MFA]: (deps) => !deps.isMFARequiredForUsers,
  [SEARCHABLE_PAGE_ID.TRANSFER_ACCOUNT]: (deps) => deps.isMemberSwitchEmployerSelfServeEnabled,

  // ============ EXTERNAL LINKS ============
  [SEARCHABLE_PAGE_ID.LYRA_APP]: (deps) => deps.showLyraApp,
}

/**
 * Mapping from page ID to toolkit enum.
 * Used for convention-based toolkit eligibility.
 */
const TOOLKIT_ID_TO_ENUM: Partial<Record<SEARCHABLE_PAGE_ID, TOOLKIT_TYPE>> = {
  [SEARCHABLE_PAGE_ID.TOOLKIT_RELATIONSHIPS]: WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS,
  [SEARCHABLE_PAGE_ID.TOOLKIT_WORK]: WELLNESS_TOOLKIT_PROMOTION.WORK,
  [SEARCHABLE_PAGE_ID.TOOLKIT_FAMILY]: WELLNESS_TOOLKIT_PROMOTION.FAMILY,
  [SEARCHABLE_PAGE_ID.TOOLKIT_NEURODIVERSITY]: CENTER_OF_EXCELLENCE.NEURODIVERSITY,
  [SEARCHABLE_PAGE_ID.TOOLKIT_HEALTH_CHALLENGES]: CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES,
  [SEARCHABLE_PAGE_ID.TOOLKIT_PARENTING]: WELLNESS_TOOLKIT_PROMOTION.PARENT,
  [SEARCHABLE_PAGE_ID.TOOLKIT_VALUES]: WELLNESS_TOOLKIT_PROMOTION.PURPOSE,
  [SEARCHABLE_PAGE_ID.TOOLKIT_TRAUMA]: WELLNESS_TOOLKIT_PROMOTION.TRAUMA,
}

/**
 * Determines if a page is eligible based on the provided dependencies.
 *
 * @param pageId - The ID of the page to check
 * @param deps - The eligibility dependencies (built from existing selectors/hooks)
 * @returns true if the page is eligible, false otherwise
 */
export const getPageEligibility = (pageId: SEARCHABLE_PAGE_ID, deps: EligibilityDeps): boolean => {
  // Check for toolkit pages using convention-based approach
  const toolkitEnum = TOOLKIT_ID_TO_ENUM[pageId]
  if (toolkitEnum) {
    return deps.toolkitAvailability[toolkitEnum] ?? false
  }

  // Check for explicit eligibility function
  const eligibilityFn = PAGE_ELIGIBILITY[pageId]
  if (eligibilityFn) {
    return eligibilityFn(deps)
  }

  // If no eligibility function defined, page is available by default (requires auth only)
  return true
}

/**
 * Filters searchable pages based on eligibility.
 *
 * @param deps - The eligibility dependencies
 * @param pages - Optional array of pages to filter. Defaults to SEARCHABLE_PAGES.
 * @returns Array of eligible searchable pages
 */
export const getEligibleSearchablePages = (
  deps: EligibilityDeps,
  pages: SearchablePageMetadata[] = SEARCHABLE_PAGES,
): SearchablePageMetadata[] => {
  return pages.filter((page) => getPageEligibility(page.id, deps))
}
